import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Mail,
    User,
    Phone,
    MessageSquare,
    Clock,
    Send,
    MapPin,
} from "lucide-react";
import { Dum_BASE_URL, messagesApi } from "../../../../api/api";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        let { name, value } = e.target;

        // Restriction conditions
        if (name === "name") {
            value = value.replace(/[^a-zA-Z\s]/g, "");
            value = value.replace(/\s{2,}/g, " ");
        }

        if (name === "email") {
            value = value.replace(/\s+/g, "");
        }

        if (name === "phone") {
            value = value.replace(/[^0-9]/g, "");
            if (value.length > 10) value = value.slice(0, 10);
            if (value.length === 1 && !/[6-9]/.test(value[0])) value = "";
        }

        if (name === "message") {
            value = value.replace(/\s{2,}/g, " ");
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const res = await axios.post(`${Dum_BASE_URL}${messagesApi}`, {
                ...formData,
            });

            if (res.status === 200 || res.status === 201) {
                toast.success("Message Sent Successfully!");
                setFormData({ name: "", email: "", phone: "", message: "" });
            } else {
                toast.error("Failed to send message. Please try again.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong! Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    const contactInfo = [
        {
            icon: <Phone size={20} />,
            label: "Phone Number",
            detail: "+91 8709364253",
            href: "tel:+918709364253",
        },
        {
            icon: <Mail size={20} />,
            label: "Email Address",
            detail: "upscpyqpedia@gmail.com",
            href: "mailto:upscpyqpedia@gmail.com",
        },
        {
            icon: <Clock size={20} />,
            label: "Office Hours",
            detail: "Mon - Sat: 9:00 AM – 7:00 PM",
            href: null,
        },
    ];

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full min-h-screen border-t border-gray-200">
            <div className="max-w-7xl mx-auto">
                {/* Standardized Editorial Header */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-16 max-w-2xl"
                >
                    <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3 tracking-widest">
                        <span className="w-6 h-1 bg-red-700"></span>
                        Support & Inquiries
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
                        Get In <span className="text-yellow-500">Touch.</span>
                    </h2>
                    <p className="text-gray-500 text-lg font-medium leading-relaxed">
                        We'd love to hear from you. Send us a message and our
                        dedicated support team will respond to your query as
                        soon as possible.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    {/* Left Column: Contact Information Blocks */}
                    <motion.div
                        className="lg:col-span-4 flex flex-col gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {contactInfo.map((info, index) => (
                            <motion.div key={index} variants={itemVariants}>
                                {info.href ? (
                                    <a href={info.href} className="group block">
                                        <InfoCard info={info} />
                                    </a>
                                ) : (
                                    <div className="group">
                                        <InfoCard info={info} />
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Right Column: Contact Form */}
                    <motion.div
                        className="lg:col-span-8 bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-10 relative overflow-hidden"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {/* Subtle Form Accent */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-yellow-400"></div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Name Input */}
                                <div>
                                    <label className="block text-sm font-bold text-neutral-900 mb-2">
                                        Full Name{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                            <User size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Your Name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Phone Input */}
                                <div>
                                    <label className="block text-sm font-bold text-neutral-900 mb-2">
                                        Phone Number{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                            <Phone size={18} />
                                        </div>
                                        <input
                                            type="tel"
                                            name="phone"
                                            placeholder="Your Phone Number"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Email Input */}
                            <div>
                                <label className="block text-sm font-bold text-neutral-900 mb-2">
                                    Email Address{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                        <Mail size={18} />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="your.email@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Message Input */}
                            <div>
                                <label className="block text-sm font-bold text-neutral-900 mb-2">
                                    Your Message{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute top-3 left-0 pl-4 pointer-events-none text-gray-400">
                                        <MessageSquare size={18} />
                                    </div>
                                    <textarea
                                        name="message"
                                        placeholder="Your Message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="5"
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all resize-none custom-scrollbar"
                                    ></textarea>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full cursor-pointer sm:w-auto flex items-center justify-center px-8 py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting
                                        ? "Sending..."
                                        : "Send Message"}
                                    {!isSubmitting && (
                                        <Send
                                            size={18}
                                            className="ml-2 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                                        />
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// Extracted Info Card Component for cleaner code
const InfoCard = ({ info }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-start gap-4 transition-all duration-300 group-hover:border-yellow-400 group-hover:shadow-md relative overflow-hidden">
        {/* Decorative Hover Background */}
        <div className="absolute inset-0 bg-yellow-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        <div className="w-12 h-12 bg-gray-50 text-neutral-900 rounded-lg flex items-center justify-center shrink-0 border border-gray-100 group-hover:bg-yellow-400 transition-colors duration-300 relative z-10 shadow-sm">
            {info.icon}
        </div>
        <div className="relative z-10 pt-1">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">
                {info.label}
            </h3>
            <p className="text-base font-bold text-neutral-900 group-hover:text-red-700 transition-colors duration-300">
                {info.detail}
            </p>
        </div>
    </div>
);

export default Contact;
